import _ from 'lodash';
import validator from 'validator';
import * as Messages from 'src/constants/ValidationMessages';
import { toFloatString } from 'src/utils/Utils';

export const isRequired = value => (value ? undefined : Messages.ERROR_MSG_IS_REQUIRED);

export const isNumber = value => {
  if (!value) return undefined;
  const converted = String(value);
  if (!converted) return undefined;
  return validator.isNumeric(converted) ? undefined : Messages.ERROR_MSG_IS_NUMERIC;
};

// validator.isNumeric (v9.2.0) only matches plain integers (`/^[-+]?[0-9]+$/`),
// so it rejects formatted decimals like "8.00" produced by toFloatFormat.
export const isNumberFormat = value => {
  if (!value) return undefined;
  const converted = String(value);
  return /^\d+(\.\d+)?$/.test(converted) ? undefined : Messages.ERROR_MSG_IS_NUMERIC;
};

export const beNumber = value => {
  if (!value) return undefined;
  const converted = String(value);
  if (!converted) return undefined;
  return validator.isNumeric(converted, { no_symbols: true }) ? undefined : Messages.ERROR_MSG_MUST_BE_NUMERIC;
};

export const isEmpty = value => (value != null ? undefined : Messages.ERROR_MSG_REVENUE_OFFICE_EMPTY);

export const isThai = value => {
  if (!value) return undefined;
  // return !/^[\u0E01-\u0E5B \s.,]+$/.test(value) ? Messages.ERROR_MSG_IS_THAI : undefined
  return !/^[\u0E01-\u0E5B \d+ \-_()（）\s.,]+$/.test(value) ? Messages.ERROR_MSG_IS_THAI : undefined;
};

export const isFloat = value => {
  if (!value) return undefined;
  const converted = String(value);
  if (!converted) return undefined;
  return converted.match(/^(?!0)\d{1,8}(\.(?!00)(\d{1,2}))?$/) ? undefined : Messages.ERROR_MSG_IS_NUMERIC;
};

export const isContributionRate = value => {
  if (!value) return undefined;
  const converted = parseFloat(value);
  if (_.isNaN(converted) || converted < 0.0 || converted > 2.0) {
    return Messages.ERROR_CONTRIBUTION_RATE;
  }

  if (!/^\d+\.\d{2}$/.test(value)) {
    return Messages.ERROR_CONTRIBUTION_RATE;
  }

  return undefined;
};

export const isThaiMoney = value => {
  if (!value) return undefined;
  if (value < 0) return Messages.ERROR_MSG_HIGHER_THAN_0;
  const converted = String(value);
  if (!converted) return undefined;
  return converted.match(/^\d+(\.(?!00)(\d{1,2}))?$/) ? undefined : Messages.ERROR_MSG_IS_NUMERIC;
};

export const isOtherTax = value => {
  if (!value) return undefined;
  if (value <= 0) return Messages.ERROR_MSG_HIGHER_THAN_0;
  const converted = String(value);
  if (!converted) return undefined;
  return converted.match(/^(?!0)\d{1,8}(\.(?!00)(\d{1,2}))?$/) ? undefined : Messages.ERROR_MSG_IS_NUMERIC;
};

export const isEnglish = value => {
  if (!value) return undefined;
  const converted = String(value);
  if (!converted) return undefined;
  return converted.match(/^[0-9a-zA-Z \-_()（）\s.,&]+$/) ? undefined : Messages.ERROR_MSG_NOT_ENGLISH;
};

export const isPercentage = value => {
  if (!value) return undefined;
  const converted = String(value);
  if (!converted) return undefined;
  return converted.match(/^\d{1,2}(\.(?!00)(\d{1,2}))?$|^100$/) ? undefined : Messages.ERROR_MSG_IS_PERCENTAGE;
};

export const moneyLength = length => value => {
  if (!value) return undefined;
  const converted = String(value);
  if (!converted) return undefined;
  const message = Messages.ERROR_MSG_MUST_BE_WITHIN_LENGTH_DIGITS(length);
  return validator.isNumeric(converted) && converted.length <= length ? undefined : validator.isNumeric(converted) ? message : Messages.ERROR_MSG_IS_NUMERIC;
};

export const isMoneyNumber = moneyLength(9);
export const isMoneyNumber10 = moneyLength(10);

export const numberLength = length => value => {
  if (!value) return undefined;
  const converted = String(value);
  if (!converted) return undefined;
  const message = Messages.ERROR_MSG_MUST_BE_WITHIN_LENGTH_DIGITS(length);
  return validator.isNumeric(converted) && converted.length <= length ? undefined : validator.isNumeric(converted) ? message : Messages.ERROR_MSG_IS_NUMERIC;
};

export const numberLength4 = numberLength(4);
export const numberLength12 = numberLength(12);

export const isPostalCode = value => {
  if (!value) return undefined;

  if (!/^\d{5}$/.test(value)) {
    return Messages.ERROR_MSG_IS_POSTAL_CODE;
  }
  return undefined;
};

export const isHHMMFormat = value => {
  if (!value) return undefined;

  if (!/^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/.test(value)) {
    return Messages.ERROR_MSG_IS_HHMM;
  }
  return undefined;
};

export const isHourFormat = value => {
  if (!value) return undefined;

  if (!/^\d{1,3}:[0-5][0-9]$/.test(value)) {
    return Messages.ERROR_MSG_IS_HOUR;
  }
  return undefined;
};

export const isFloatFormat = value => {
  if (!value) return undefined;

  if (!/^\d{1,3}\.\d{2}$/.test(value)) {
    return Messages.ERROR_MSG_IS_DATE;
  }
  return undefined;
};

export const isInsuranceRateFormat = value => {
  if (!value) return undefined;

  if (!/^\d{1,3}\.\d{1,3}$/.test(value)) {
    return Messages.ERROR_MSG_IS_INSURANCE_RATE;
  }
  return undefined;
};

export const isAccidentInsuranceRateFormat = value => {
  const val = toFloatString(value);
  if (!val) return undefined;
  if (!/^\d{0,3}(\.\d{0,4})?$/.test(val)) {
    return Messages.ERROR_MSG_IS_INSURANCE_RATE;
  }
  return undefined;
};

// Format: DD/MM/YYYY or DD-MM-YYYY
export const isDateStringFormat = value => {
  if (!value) return undefined;

  if (/^\d{1,2}[/-]\d{1,2}[/-]\d{4}$/.test(value) || /^\d{4}[/-]\d{1,2}[/-]\d{1,2}$/.test(value)) {
    return undefined;
  }

  return Messages.ERROR_MSG_IS_DATE_STRING;
};

export const isYearFormat = value => {
  if (!value) return undefined;

  if (!/^\d{4}$/.test(value)) {
    return Messages.ERROR_MSG_IS_DATE_STRING;
  }
  return undefined;
};

export const isBranchCode = value => {
  if (!value) return undefined;

  if (!/^\d{5}$/.test(value)) {
    return Messages.INVALID_BRANCH_CODE;
  }
  return undefined;
};

export const maxLength = max => value => (value && value.length > max ? Messages.MAX_LENGTH(max) : undefined);

export const maxLength3 = maxLength(3);
export const maxLength4 = maxLength(4);
export const maxLength5 = maxLength(5);
export const maxLength6 = maxLength(6);
export const maxLength7 = maxLength(7);
export const maxLength8 = maxLength(8);
export const maxLength9 = maxLength(9);
export const maxLength10 = maxLength(10);
export const maxLength12 = maxLength(12);
export const maxLength15 = maxLength(15);
export const maxLength20 = maxLength(20);
export const maxLength25 = maxLength(25);
export const maxLength30 = maxLength(30);
export const maxLength37 = maxLength(37);
export const maxLength50 = maxLength(50);
export const maxLength100 = maxLength(100);
export const maxLength255 = maxLength(255);
export const maxLength1500 = maxLength(1500);

export const exceededLength = max => value => (value && value.length > max ? Messages.EXCEEDED_LENGTH(max, value.length - max) : undefined);

export const exceededLength1500 = exceededLength(1500);

export const exactLength = length => value => (!value || value.length === length ? undefined : Messages.EXACT_LENGTH(length));
export const exactLength5 = exactLength(5);
export const exactLength6 = exactLength(6);
export const exactLength10 = exactLength(10);
export const exactLength12 = exactLength(12);

export const minLength = min => value => (value && value.length < min ? Messages.MIN_LENGTH(min) : undefined);
export const minLength2 = minLength(2);
export const minLength4 = minLength(4);
export const minLength6 = minLength(6);
export const minValue = min => value => (value && value < min ? Messages.MIN_VALUE(min) : undefined);
export const minValue0 = minValue(0);
export const minValue1 = minValue(1);
export const minValue18 = minValue(18);
export const isEmail = value => (value && !/^([^@\s]+)@((?:[a-zA-Z0-9_-]+\.)+[a-zA-Z]{2,})$/i.test(value) ? Messages.INVALID_EMAIL : undefined);
export const capitalAlphaNumericSymbol = value => (value && /[^A-Z0-9_<>&~!@()/{}:;#%-]/.test(value) ? Messages.CAPITAL_ALPHANUMERIC_SYMBOL : undefined);
export const isStaffCode = value => (value && /[^a-zA-Z0-9_-]/i.test(value) ? Messages.INVALID_STAFF_CODE : undefined);
export const isPersonalNumber = value => (value && !/^\d{9,13}$/.test(value) ? Messages.INVALID_PERSONAL_ID : undefined);
export const alphaNumeric = value => (value && /[^a-zA-Z0-9 ]/i.test(value) ? Messages.ONLY_ALPHANUMERIC : undefined);
export const isThaiCitizenID = value => (value && !/^(\d{1,1})(\d{1,4})(\d{1,5})(\d{1,2})(\d{1})$/i.test(value) ? 'ID' : undefined);
export const phoneNumber = value => (value && !/^(0[0-9]{8,9})$/i.test(value) ? Messages.INVALID_PHONE : undefined);
export const digitFloatingNumber = value => (value && !/^[\d]{1,10}\.?[\d]{0,10}$/i.test(value) ? Messages.ONLY_DIGIT_FLOATING_NUMBER : undefined);
export const insuranceRate = value => (value && !/^[\d]{1,10}\.?[\d]{0,10}$/i.test(value) ? Messages.INVALID_INSURANCE_RATE : undefined);
export const zenkakuKatakana = value => (value && !value.match(/^[\u30A0-\u30FF\x20\u3000]+$/) ? Messages.ZENKAKU_KATAKANA : undefined);
export const zenkakuKatakanaLoose = value =>
  value && value.match(/[\u4E00-\u9FFF\u3005-\u3006\u3040-\u309f]/) ? 'を全角カタカナとスペースで入力してください' : undefined;
// 英数字、カタカナ(半角・全角)、括弧(半角・全角)のみ許可
// railsのバリデーションと合わせているため改修するときは、一緒に修正すること
// see: app/validators/bank_kana_validator.rb
export const bankKana = value =>
  // ESLint not allow to use irregular while space in regex
  // eslint-disable-next-line no-irregular-whitespace
  value && !value.match(/^[ｦ-ﾟァ-ヴ-ー－ 　()（）\u0E01-\u0E5B 0-9a-zA-Z０-９ａ-ｚＡ-Ｚ./]+$/) ? Messages.ERROR_MSG_BANK_KANA : undefined;

export const validBankName = value => {
  if (!value) return undefined;
  const regex = /[^a-zA-Z0-9\s.,()-]+/g;
  const checkMatchChar = value.match(regex);
  if (checkMatchChar) {
    return `${Messages.ERROR_MSG_BANK_THAI_ENG}\n ${Messages.ERROR_MSG_CHARACTERS_CANNOT_BE_USED(checkMatchChar)}`;
  }
  return undefined;
};

// Validate Thai bank
export const bankThaiEng = value => (value && !value.match(/^[a-zA-Z0-9\s.,()-]+$/) ? validBankName(value) : undefined);
// http://www.houjin-bangou.nta.go.jp/documents/checkdigit.pdf
const validCorporateNumber = digits => {
  if (!digits.match(/^\d{10}$/)) {
    return false;
  }

  return true;
};

export const isCorporateNumber = value => (value && !validCorporateNumber(value) ? Messages.CORP_NUMBER : undefined);

export const isCorporateNumberForBranch = value => {
  if (value && !value.match(/^\d{10}-\d{3}$/)) {
    return Messages.CORP_NUMBER;
  }

  return undefined;
};

export const isSocialSecurityNumber = value => (value && !value.match(/^[A-Z0-9]{7}$/) ? Messages.ERROR_MSG_SOCIAL_SECURITY_NUMBER : undefined);

// 住民税納付先：指定番号
// https://github.com/d-o-n-u-t-s/payrollservice/issues/5104
export const designationNumber = value => (value && !value.match(/^[a-zA-Z0-9ｦ-ﾝﾞﾟ\--]*$/) ? Messages.ERROR_MSG_DESIGNATION_NUMBER : undefined);

const OPEN_CHARS = ['(', '【'];
const CLOSE_CHARS = [')', '】'];
const OP_CHARS = ['+', '-', '*', '/'];
const NEED_CHECKING_CHARS = OPEN_CHARS.concat(CLOSE_CHARS).concat(OP_CHARS);
const PREV_CHARS = {
  ')': ['('],
  '】': ['【']
};
const isPercentChar = char => char === '%';
const isOpenChar = char => OPEN_CHARS.includes(char);
const isCloseChar = char => CLOSE_CHARS.includes(char);
const isOperationChar = char => OP_CHARS.includes(char);
const validOpenChar = (closeChar, char) => (PREV_CHARS[closeChar] || []).includes(char);
const isSpace = char => char === ' ';
const isNormalChar = char => !NEED_CHECKING_CHARS.includes(char);
const allNormalChars = chars => !chars.find(char => !isNormalChar(char));

// Lightweight structural check for IF(condition, value_if_true, value_if_false):
// every IF(...) group must close, contain exactly two top-level commas (3 parts),
// and have no empty part. Not a full parser — backend validation stays authoritative.
const validIfStructure = value => {
  let from = value.indexOf('IF(');

  while (from !== -1) {
    let depth = 0;
    let commas = 0;
    let closed = false;
    let pos = from + 2; // the '(' that opens this IF
    const parts = [''];

    for (; pos < value.length; pos += 1) {
      const char = value[pos];
      if (char === '(') {
        depth += 1;
        if (depth > 1) parts[parts.length - 1] += char;
      } else if (char === ')') {
        depth -= 1;
        if (depth === 0) {
          closed = true;
          break;
        }
        parts[parts.length - 1] += char;
      } else if (char === ',' && depth === 1) {
        commas += 1;
        parts.push('');
      } else {
        parts[parts.length - 1] += char;
      }
    }

    if (!closed || commas !== 2 || parts.some(part => part.trim() === '')) return false;
    from = value.indexOf('IF(', pos + 1);
  }

  return true;
};

const validFormula = value => {
  const chars = value.split('').filter(char => !isSpace(char));

  if (allNormalChars(chars)) return false;

  if (!validIfStructure(value)) return false;

  const openChars = [];
  let currentChar;
  let prevChar;

  for (let i = 0; i < chars.length; i += 1) {
    currentChar = chars[i];

    if (isPercentChar(currentChar)) return false;

    if (isOpenChar(currentChar)) {
      if (isCloseChar(prevChar)) return false;
      openChars.push(currentChar);
    } else if (isCloseChar(currentChar)) {
      const openChar = openChars.pop();
      if (!validOpenChar(currentChar, openChar)) return false;
    } else if (isOperationChar(currentChar)) {
      if (isOperationChar(prevChar)) return false;
    }

    prevChar = currentChar;
  }

  return openChars.length === 0;
};

export const isFormula = value => (value && !validFormula(value) ? Messages.INVALID_FORMULA : undefined);

export const itemsChecked = value => (Array.isArray(value) && value.length > 0 ? undefined : Messages.REQUIRED_TO_CHECK);
export const isYYYYMMDDFormat = value => (value && !/^\d{1,2}[/-]\d{1,2}[/-]\d{4}$/.test(value) ? Messages.INVALID_DDMMYYYY : undefined);

export const isBasicDaysFormat = value => {
  if (!value) return undefined;
  const converted = Number(value);
  if (!converted) return undefined;
  return converted >= 0 && converted <= 31 ? undefined : Messages.ERROR_MSG_IS_BASIC_DAYS;
};

export const selectedInviteRole = value => (['admin', 'operator'].includes(value) ? undefined : Messages.ERROR_MSG_IS_SELECTED);

export const isRequiredNotNegativeMoneyNumber = value => {
  if (value === '' || (validator.isNumeric(String(value)) && value < 0)) return Messages.ERROR_MSG_NOT_NEGATIVE_MONY_NUMBER;
  return isMoneyNumber(value);
};

export const isProbationDateValid = (probationDate, joinedOnDate) => {
  if (!probationDate) return undefined;
  if (isDateStringFormat(probationDate)) return Messages.ERROR_MSG_IS_DATE_STRING;
  if (isDateStringFormat(joinedOnDate)) return Messages.ERROR_MSG_IS_DATE_STRING;

  const probationDateEnd = new Date(probationDate);
  const joinOn = new Date(joinedOnDate);

  if (probationDateEnd < joinOn) return Messages.ERROR_MSG_MUST_BE_AFTER_JOIN_DATE;

  return undefined;
};

export const isProbationDaysValid = value => {
  if (!value) return undefined;
  const converted = String(value);
  if (parseInt(converted, 10) < 0 || !validator.isNumeric(converted)) return Messages.ERROR_MSG_PROBATION_PERIOD;
  return undefined;
};
